import './TagInput.css';
import React, { ChangeEvent, FC, useRef, useState } from 'react';
import classNames from 'classnames';
import { Input, Tag, Tooltip } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { uniqueArray } from '@vindr/utils/helpers/common.helper';
import { useTranslation } from 'react-i18next';

type TagInputProps = {
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  required?: boolean;
  message: MessageInstance;
};

const acceptKey = ['Enter', 'Tab', ','];

const TagInput: FC<TagInputProps> = ({
  value = [],
  onChange,
  placeholder,
  disabled,
  required,
  message,
  ...props
}) => {
  const [content, setContent] = useState<string>();
  const [showClear, setShowClear] = useState<boolean>(false);
  const inputRef = useRef(null);
  const { t } = useTranslation('Vindoc');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
  };

  const handleBlur = () => {
    if (content) {
      if (value.includes(content)) {
        message.error(t(`[tag: ${content}] already exists`));

        return;
      }
      onChange?.([...value, content]);
      setContent('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) =>
    acceptKey.includes(e.key) && e.preventDefault();

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (acceptKey.includes(e.key) && content) {
      if (value.includes(content)) {
        message.error(t(`[tag: ${content}] already exists`));

        return;
      }

      const newContent = content.split(/[,，]/).map((item) => item.trim()); //? When paste content, split by comma

      onChange?.(uniqueArray([...value, ...newContent]));
      setContent('');
    }
  };

  const handleClear = () => {
    onChange?.([]);
  };

  return (
    <div
      className={classNames(`tagInput flex`, {
        'pl-[11px]': !value.length,
        'pl-[5px]': value.length,
      })}
      onClick={() => inputRef.current?.focus()}
      onMouseOver={() => setShowClear(true)}
      onMouseLeave={() => setShowClear(false)}
    >
      <section className="flex-1">
        {value.map((item) =>
          item.length > 20 ? (
            <Tooltip title={item} key={item}>
              <Tag closable>{`${item.slice(0, 20)}...`}</Tag>
            </Tooltip>
          ) : (
            <Tag closable key={item} onClose={() => onChange?.(value.filter((i) => i !== item))}>
              {item}
            </Tag>
          )
        )}
        <Input
          ref={inputRef}
          bordered={false}
          placeholder={!value.length ? placeholder : ''}
          className="input"
          value={content}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          onKeyUp={handleKeyUp}
          {...{ disabled, required }}
          {...props}
        />
      </section>
      <section className="m-auto w-5 text-center">
        <span
          className={classNames('clear', {
            'hidden': !showClear || !value.length,
          })}
          onClick={handleClear}
        >
          <span
            role="button"
            aria-label="close-circle"
            // tabIndex="-1"
          >
            <svg
              viewBox="64 64 896 896"
              focusable="false"
              data-icon="close-circle"
              width="1em"
              height="1em"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm165.4 618.2l-66-.3L512 563.4l-99.3 118.4-66.1.3c-4.4 0-8-3.5-8-8 0-1.9.7-3.7 1.9-5.2l130.1-155L340.5 359a8.32 8.32 0 01-1.9-5.2c0-4.4 3.6-8 8-8l66.1.3L512 464.6l99.3-118.4 66-.3c4.4 0 8 3.5 8 8 0 1.9-.7 3.7-1.9 5.2L553.5 514l130 155c1.2 1.5 1.9 3.3 1.9 5.2 0 4.4-3.6 8-8 8z"></path>
            </svg>
          </span>
        </span>
      </section>
    </div>
  );
};

export default TagInput;
